import jwtDecode from 'jwt-decode';
import axios from 'axios';
import config from '../../config';

// content type
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.baseURL = config.REACT_APP_API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let message;
        
        if (error.message == 'Network Error') {
            Promise.reject(error.message);
        } else if (error && error.response && error.response.status === 404) {
            window.location.href = '/ErrorPageNotFound';
        } else if (error && error.response && error.response.status === 403) {
            // window.location.href = '/access-denied';
        } else {
            switch (error.response.status) {
                // case 401:
                //     message = 'Invalid credentials';
                //     break;
                case 403:
                    message = 'Acceso denegado';
                    break;
                case 404:
                    message = 'Lo sentimos! No se encontraron los datos buscados...';
                    break;
                default: {
                    message =
                        error.response && error.response.data ? error.response.data['message'] : error.message || error;
                }
            }
            return Promise.reject(message);
        }
    }
);

const AUTH_SESSION_KEY = 'ei_user_data';

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    else delete axios.defaults.headers.common['Authorization'];
};

const getUserFromSession = () => {
    const session = sessionStorage.getItem(AUTH_SESSION_KEY);
    const user = session ? (typeof session == 'object' ? session : JSON.parse(session)) : null;
    return user;
};

export interface APICoreInterface {
    get: (url: string, params: any) => any;
    getFile: (url: string, params: any) => any;
    getMultiple: (url: string, params: any) => any;
    post: (url: string, data: any) => any;
    create: (url: string, data: any) => any;
    updatePatch: (url: string, data: any) => any;
    update: (url: string, data: any) => any;
    delete: (url: string) => any;
    createWithFile: (url: string, data: any) => any;
    updateWithFile: (url: string, data: any) => any;
    isUserAuthenticated: () => any;
    setLoggedInUser: (session: any) => any;
    getLoggedInUser: () => any;
    setUserInSession: (modifiedUser: any) => any;
};

class APICore implements APICoreInterface {

    /**
     * Fetches data from given url
     */
    get = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
            response = axios.get(`${url}?${queryString}`, params);
        } else {
            response = axios.get(`${url}`, params);
        }
        return response;
    };

    getFile = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
            response = axios.get(`${url}?${queryString}`, { responseType: 'blob' });
        } else {
            response = axios.get(`${url}`, { responseType: 'blob' });
        }
        return response;
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = '';
        if (params) {
            queryString = params
                ? Object.keys(params)
                    .map((key) => key + '=' + params[key])
                    .join('&')
                : '';
        }

        for (const url of urls) {
            reqs.push(axios.get(`${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    post = (url: string, data: any) => {
        return axios.post(url, data);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any) => {
        return axios.post(url, data);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config: any = {
            headers: {
                ...axios.defaults.headers,
                'content-type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user = getUserFromSession();

        if (user == null) {
            return false;
        }
        const decoded: any = jwtDecode(user?.token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
            console.warn('access token expired');
            return false;
        } else {
            return true;
        }
    };

    setLoggedInUser = (session: any) => {
        if (session) {
            sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        }
        else {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromSession();
    };

    setUserInSession = (modifiedUser: any) => {
        let userInfo = getUserFromSession();
        if (userInfo) {
            if (userInfo instanceof String) {
                const { token, user } = JSON.parse(userInfo as string);
                this.setLoggedInUser({ token, ...user, ...modifiedUser });
            }
            else {
                const { token, user } = userInfo;
                this.setLoggedInUser({ token, ...user, ...modifiedUser });
            }
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromSession();
if (user) {
    const { token } = user;
    if (token) {
        setAuthorization(token);
    }
}

export { APICore, setAuthorization };
